import { useEffect, useState } from 'react';
import { getFaceUserProfile } from '../utils/faceIdentityApi';
import { resolveFacePortraitUrl } from '../utils/facePortrait';
import { getSalonAdminApiBaseUrl } from '../../salon-admin/utils/apiConfig';
import { franchiseAuthHeaders } from '../../shared/franchiseFlowAuth';
import { deriveGuestWelcomeVisit, previousServicesFromProfileHistory } from '../utils/guestWelcomeModel';

export function useGuestWelcomePanelData(faceUserId, initialProfile, initialUser) {
  const [profile, setProfile] = useState(initialProfile || initialUser?.profile || null);
  const [portraitUrl, setPortraitUrl] = useState('');
  const [previousServices, setPreviousServices] = useState([]);

  useEffect(() => {
    setProfile(initialProfile || initialUser?.profile || null);
    const initialPortrait = resolveFacePortraitUrl(initialUser, initialProfile);
    if (initialPortrait) setPortraitUrl(initialPortrait);
  }, [initialProfile, initialUser]);

  useEffect(() => {
    let cancelled = false;
    const id = String(faceUserId || '').trim();
    if (!id) return undefined;
    (async () => {
      try {
        const data = await getFaceUserProfile(id);
        const nextProfile = data?.profile || data?.user?.profile || null;
        if (!cancelled && nextProfile) {
          setProfile((prev) => ({
            ...(prev || {}),
            ...nextProfile,
            history_summary: {
              ...(prev?.history_summary || {}),
              ...(nextProfile.history_summary || {}),
            },
            history: nextProfile.history || prev?.history,
          }));
        }
        const nextPortrait = resolveFacePortraitUrl(data?.user, data?.profile, data);
        if (!cancelled && nextPortrait) setPortraitUrl(nextPortrait);
      } catch {
        /* keep snapshot */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [faceUserId]);

  useEffect(() => {
    let cancelled = false;
    const id = String(faceUserId || '').trim();
    if (!id) {
      setPreviousServices([]);
      return undefined;
    }
    (async () => {
      try {
        const base = (getSalonAdminApiBaseUrl() || '').replace(/\/$/, '');
        if (!base) throw new Error('no api');
        const headers = franchiseAuthHeaders({ Accept: 'application/json' });
        const res = await fetch(`${base}/register-services/history/${encodeURIComponent(id)}`, {
          headers,
          credentials: 'include',
        });
        const data = await res.json().catch(() => ({}));
        if (res.ok && Array.isArray(data.items) && data.items.length) {
          if (!cancelled) setPreviousServices(data.items);
          return;
        }
        throw new Error('empty');
      } catch {
        if (!cancelled) setPreviousServices(previousServicesFromProfileHistory(profile));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [faceUserId, profile]);

  const visit = deriveGuestWelcomeVisit(profile);
  const displayName =
    String(initialUser?.name || initialUser?.full_name || 'Guest').trim() || 'Guest';
  const initial = displayName.charAt(0).toUpperCase() || 'G';

  return {
    profile,
    portraitUrl,
    previousServices,
    visit,
    displayName,
    initial,
    isNewMember: visit.isNewMember,
  };
}
